import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { UptimeForm } from '@/settings/uptime/form'
import { ErrorState, Loading } from '@/ui/states'
import { NoSiteYet, SettingsStepShell } from './SettingsStepShell'
import { useWizard } from './context'
import { useSettleStep } from './settingsStep'

/** Step 11 — uptime checks for the wizard's site, through `/settings/uptime`'s form. */
export function UptimeStep(): JSX.Element {
  const state = useWizard()
  const site = state.site ?? null
  const siteId = site?.id ?? null
  const uptime = useQuery(
    (signal) =>
      siteId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/uptime', {
              params: { path: { site_id: siteId } },
              signal,
            }),
          ),
    [siteId],
  )
  const save = useMutation((body: Schema<'MonitorIn'>) =>
    call(() =>
      api.PUT('/api/v1/seo/sites/{site_id}/uptime', {
        params: { path: { site_id: siteId ?? '' } },
        body,
      }),
    ),
  )
  const stop = useMutation(() =>
    call(() =>
      api.DELETE('/api/v1/seo/sites/{site_id}/uptime', {
        params: { path: { site_id: siteId ?? '' } },
      }),
    ),
  )
  const { settle, failure } = useSettleStep('uptime')

  return (
    <SettingsStepShell step="uptime" failure={failure}>
      {site === null ? <NoSiteYet /> : null}
      {site !== null && uptime.state.status === 'loading' ? (
        <Loading label="Loading" lines={2} />
      ) : null}
      {uptime.state.status === 'error' ? (
        <ErrorState failure={uptime.state.failure} onRetry={uptime.refetch} />
      ) : null}
      {site !== null && uptime.state.status === 'ready' && uptime.state.data !== null ? (
        <UptimeForm
          monitor={uptime.state.data.monitor}
          domain={site.domain}
          submitLabel="Continue"
          onSubmit={async (choice) => {
            // *Off* on a site that was never watched has nothing to delete —
            // the default is what Skip would leave, so Continue just settles.
            const result =
              choice.kind === 'off'
                ? uptime.state.status === 'ready' && uptime.state.data?.monitor === null
                  ? { ok: true as const, data: undefined }
                  : await stop.run(undefined)
                : await save.run({
                    url: choice.url,
                    enabled: choice.enabled,
                    interval_seconds: choice.seconds,
                  })
            if (result.ok) await settle()
            return result
          }}
        />
      ) : null}
    </SettingsStepShell>
  )
}
